import { Button, InputNumber, InputCurrency } from "@sellar-io/components";
import { useEffect, useId } from "react";
import { InputLabel, InputContainer } from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
  DialogCTA,
} from "../../../components/modal/Dialog";
import { ProductPriceTierDerivationType } from "@sellar-io/api-v2/types";
import { PriceListPricingMap } from "../priceListStore";
import { twMerge } from "tailwind-merge";
import {
  formatPriceApiToInput,
  formatPriceInputToApi,
} from "../../../hooks/usePriceFormat";
import { useForm, useWatch } from "react-hook-form";
import { toNumberOrNull } from "@sellar-io/common";

const formatDerivationValueForInput = (
  value: number | null,
  type?: ProductPriceTierDerivationType,
) => {
  if (value == null) return null;
  if (type === "flat") {
    return formatPriceApiToInput(value);
  }
  return value;
};

const formatDerivationValueForApi = (
  value: number | string | null,
  type?: ProductPriceTierDerivationType,
) => {
  if (value == null || type === "custom" || value === "") return null;
  if (type === "flat") {
    return formatPriceInputToApi(value);
  }
  return toNumberOrNull(value);
};

export const PriceRuleUpdateDialog = ({
  pricing,
  active,
  setActive,
  updateDerivedPrice,
}: {
  pricing?: PriceListPricingMap[number];
  active: boolean;
  setActive: (active: boolean) => void;
  updateDerivedPrice: (params: {
    derivationType: ProductPriceTierDerivationType;
    derivationValue: number | null;
    price?: number | null;
  }) => void;
}) => {
  const form = useForm<{
    derivationType?: ProductPriceTierDerivationType;
    derivationValue: string | number | null;
    price: string | null;
  }>({
    shouldUseNativeValidation: true,
    defaultValues: {
      derivationType: pricing?.derivationType,
      derivationValue: formatDerivationValueForInput(
        pricing?.derivationValue ?? null,
        pricing?.derivationType,
      ),
      price:
        pricing?.price !== null && pricing?.price !== undefined
          ? formatPriceApiToInput(pricing.price)
          : null,
    },
  });

  const { reset } = form;

  useEffect(() => {
    reset({
      derivationType: pricing?.derivationType,
      derivationValue: formatDerivationValueForInput(
        pricing?.derivationValue ?? null,
        pricing?.derivationType,
      ),
      price:
        pricing?.price !== null && pricing?.price !== undefined
          ? formatPriceApiToInput(pricing.price)
          : null,
    });
  }, [pricing, reset]);

  const derivationType = useWatch({
    control: form.control,
    name: "derivationType",
  });
  const formId = useId();

  const onSubmit = (data: {
    derivationType?: ProductPriceTierDerivationType;
    derivationValue: string | number | null;
    price: string | null;
  }) => {
    if (!data.derivationType) return;

    updateDerivedPrice({
      derivationType: data.derivationType,
      derivationValue: formatDerivationValueForApi(
        data.derivationValue,
        data.derivationType,
      ),
      price:
        data.price !== null && data.price !== ""
          ? formatPriceInputToApi(data.price)
          : null,
    });

    setActive(false);
  };

  const onDismiss = () => setActive(false);

  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        leftIcon="CHEVRON_DOWN"
        onClickLeftIcon={onDismiss}
        title="Edit item value"
        description="Give this product a fixed value, or reduce it by a flat or percentage amount"
      />

      <DialogContent className="flex flex-col gap-4">
        <form id={formId} onSubmit={form.handleSubmit(onSubmit)}>
          <div className="flex">
            <Button
              color="secondary"
              className={twMerge(
                "w-full rounded-r-none bg-white",
                derivationType === "custom" &&
                  "pointer-events-none bg-zinc-100",
              )}
              onClick={() => form.setValue("derivationType", "custom")}
            >
              Fixed
            </Button>
            <Button
              color="secondary"
              className={twMerge(
                "w-full rounded-none border-x-0 bg-white",
                derivationType === "percentage" &&
                  "pointer-events-none bg-zinc-100",
              )}
              onClick={() => form.setValue("derivationType", "percentage")}
            >
              % amount off
            </Button>
            <Button
              color="secondary"
              className={twMerge(
                "w-full rounded-l-none bg-white",
                derivationType === "flat" && "pointer-events-none bg-zinc-100",
              )}
              onClick={() => form.setValue("derivationType", "flat")}
            >
              £ amount off
            </Button>
          </div>
          <InputLabel>
            {derivationType === "custom"
              ? "Set fixed price"
              : derivationType === "percentage"
                ? "Discount amount (%)"
                : derivationType === "flat"
                  ? "Discount amount (£)"
                  : "Select a type"}
          </InputLabel>
          <InputContainer>
            {derivationType === "custom" && (
              <InputCurrency
                showPrefix
                allowNegative
                {...form.register("price", {
                  required: true,
                  valueAsNumber: true,
                })}
              />
            )}
            {derivationType === "percentage" && (
              <>
                <span>%</span>
                <InputNumber
                  {...form.register("derivationValue", {
                    required: true,
                    valueAsNumber: true,
                  })}
                  min={0}
                  max={100}
                  step={0.1}
                />
              </>
            )}
            {derivationType === "flat" && (
              <InputCurrency
                showPrefix
                allowNegative
                {...form.register("derivationValue", {
                  required: true,
                  valueAsNumber: true,
                })}
              />
            )}
          </InputContainer>
        </form>
      </DialogContent>
      <DialogCTA>
        <Button form={formId} type="submit">
          Confirm
        </Button>
      </DialogCTA>
    </Dialog>
  );
};
